import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaClock,
  FaCalendarDay,
  FaTicketAlt,
  FaCar,
  FaBullhorn,
} from "react-icons/fa";
import axiosInstance from "../../utils/axiosConfig";

interface AdminStats {
  totalEmployees: number;
  presentToday: number;
  absentToday: number;
  lateToday: number;
  onLeaveToday: number;
  halfDayToday: number;
  earlyOutToday: number;
  pendingLeaves: number;
  openTickets: number;
  totalVehicles: number;
  totalNotices: number;
}

const AdminAttendanceStats: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log("AdminAttendanceStats: Fetching stats...");
        const { data } = await axiosInstance.get("/api/attendance/admin/stats");
        console.log("AdminAttendanceStats: Received data:", data);
        setStats(data);
      } catch (error) {
        console.error("AdminAttendanceStats: Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="h-24 bg-platinum-50 animate-pulse rounded-xl border border-platinum-100"
          ></div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const cards = [
    {
      label: "Total Employees",
      value: stats.totalEmployees,
      icon: FaUsers,
      color: "text-indigo-600",
      bg: "bg-indigo-50",
      borderColor: "border-indigo-100",
    },
    {
      label: "Present Today",
      value: stats.presentToday,
      icon: FaUserCheck,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      borderColor: "border-emerald-100",
    },
    {
      label: "Absent Today",
      value: stats.absentToday,
      icon: FaUserTimes,
      color: "text-rose-600",
      bg: "bg-rose-50",
      borderColor: "border-rose-100",
    },
    {
      label: "Late Arrivals",
      value: stats.lateToday,
      icon: FaClock,
      color: "text-amber-600",
      bg: "bg-amber-50",
      borderColor: "border-amber-100",
    },
    {
      label: "Pending Leaves",
      value: stats.pendingLeaves,
      icon: FaCalendarDay,
      color: "text-sky-600",
      bg: "bg-sky-50",
      borderColor: "border-sky-100",
    },
    {
      label: "Open Tickets",
      value: stats.openTickets,
      icon: FaTicketAlt,
      color: "text-purple-600",
      bg: "bg-purple-50",
      borderColor: "border-purple-100",
    },
    {
      label: "Total Vehicles",
      value: stats.totalVehicles,
      icon: FaCar,
      color: "text-slate-grey-600",
      bg: "bg-slate-grey-50",
      borderColor: "border-slate-grey-100",
    },
    {
      label: "Active Notices",
      value: stats.totalNotices,
      icon: FaBullhorn,
      color: "text-teal-600",
      bg: "bg-teal-50",
      borderColor: "border-teal-100",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {cards.map((card, i) => (
        <div
          key={i}
          className={`relative overflow-hidden p-5 rounded-2xl border ${card.borderColor} bg-white shadow-sm hover:shadow-md transition-all duration-300 group`}
        >
          <div
            className={`absolute top-0 right-0 w-16 h-16 ${card.bg} opacity-20 rounded-bl-[100px] transition-transform duration-500 group-hover:scale-110`}
          ></div>
          <div className="flex flex-col gap-3 relative z-10">
            <div
              className={`w-10 h-10 rounded-xl ${card.bg} ${card.color} flex items-center justify-center shadow-sm`}
            >
              <card.icon size={18} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-grey-400 uppercase tracking-[0.15em] mb-1">
                {card.label}
              </p>
              <p className="text-2xl font-black text-gunmetal-900 tabular-nums">
                {card.value}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminAttendanceStats;
